import { useState } from 'react';
import { formatCurrency } from '../utils/calculations';
import { GOOGLE_API_KEY } from '../utils/googleMaps';

function parseAddress(address = '') {
  // Expects: "123 Main St, City, ST 85001" or "123 Main St, City, State ZIP, USA"
  const parts = address.split(',').map(s => s.trim());
  const street = parts[0] || '';
  const city   = parts[1] || '';
  const stateZip = (parts[2] || '').replace(/\bUSA?\b/i, '').trim();
  const stateMatch = stateZip.match(/^([A-Za-z\s]+?)\s*(\d{5}(?:-\d{4})?)?$/);
  const state  = stateMatch ? stateMatch[1].trim() : stateZip.split(' ')[0];
  const zip    = stateMatch ? (stateMatch[2] || '') : (stateZip.match(/\d{5}/) || [''])[0];
  return { street, city, state, zip };
}

export default function ContractPDF({ dealData }) {
  const [generating, setGenerating] = useState(false);

  const generatePDF = async () => {
    if (!dealData) return;
    setGenerating(true);

    try {
      const { default: jsPDF }     = await import('jspdf');
      const { default: autoTable } = await import('jspdf-autotable');

      const doc   = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const L = 20, R = pageW - 20, TW = R - L;
      const mid = pageW / 2;

      const BLACK  = [0, 0, 0];
      const DGRAY  = [40, 40, 40];
      const MGRAY  = [90, 90, 90];
      const LGRAY  = [200, 200, 200];
      const WHITE  = [255, 255, 255];

      const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const price   = formatCurrency(dealData.recommendedOffer || dealData.mao || dealData.purchasePrice || 0);
      const { street: parsedStreet, city: parsedCity, state: parsedState, zip: parsedZip } = parseAddress(dealData.address);

      // ── Footer ───────────────────────────────────────────────
      const addFooter = (pageNum) => {
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...MGRAY);
        doc.text('REAL ESTATE PURCHASE CONTRACT  ·  ProPARV', L, pageH - 8);
        doc.text(`Page ${pageNum}`, R, pageH - 8, { align: 'right' });
        doc.setDrawColor(...LGRAY);
        doc.setLineWidth(0.3);
        doc.line(L, pageH - 11, R, pageH - 11);
      };

      let page = 1;

      const checkPage = (neededY) => {
        if (neededY > pageH - 20) {
          addFooter(page);
          doc.addPage();
          page++;
          // Continuation header
          doc.setFontSize(9);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...DGRAY);
          doc.text('REAL ESTATE PURCHASE CONTRACT (continued)', L, 14);
          doc.setDrawColor(...BLACK);
          doc.setLineWidth(0.5);
          doc.line(L, 17, R, 17);
          return 24;
        }
        return neededY;
      };

      // ─────────────────────────────────────────────────────────
      // PAGE 1
      // ─────────────────────────────────────────────────────────

      // Title block
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text('REAL ESTATE PURCHASE CONTRACT', mid, 22, { align: 'center' });

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MGRAY);
      doc.text('This is a legally binding contract. Read all provisions carefully before signing.', mid, 30, { align: 'center' });

      doc.setDrawColor(...BLACK);
      doc.setLineWidth(1);
      doc.line(L, 34, R, 34);
      doc.setLineWidth(0.3);
      doc.line(L, 36, R, 36);

      let y = 44;

      // ── Section helper ───────────────────────────────────────
      const section = (title, yPos) => {
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...BLACK);
        doc.text(title, L, yPos);
        doc.setDrawColor(...DGRAY);
        doc.setLineWidth(0.5);
        doc.line(L, yPos + 2, R, yPos + 2);
        return yPos + 8;
      };

      const blankField = (label, xStart, width, yPos, value = '') => {
        doc.setDrawColor(...BLACK);
        doc.setLineWidth(0.4);
        doc.line(xStart, yPos, xStart + width, yPos);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...MGRAY);
        doc.text(label, xStart, yPos + 4);
        if (value) {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...DGRAY);
          doc.text(value, xStart, yPos - 2);
        }
      };

      const bodyText = (text, yPos, indent = 0) => {
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...DGRAY);
        const lines = doc.splitTextToSize(text, TW - indent);
        doc.text(lines, L + indent, yPos);
        return yPos + lines.length * 5.5 + 3;
      };

      // ── 1. DATE & PARTIES ────────────────────────────────────
      y = section('1.  PARTIES AND DATE', y);

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DGRAY);
      doc.text('This Real Estate Purchase Contract ("Agreement") is entered into as of ', L, y);
      doc.setFont('helvetica', 'bold');
      doc.text(dateStr, L + 86, y);
      doc.setFont('helvetica', 'normal');
      doc.text(', by and between:', L + 86 + doc.getTextWidth(dateStr), y);
      y += 10;

      // Seller block
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text('SELLER:', L, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DGRAY);
      y += 6;

      const half = TW / 2 - 5;
      blankField('Seller Full Legal Name', L, TW, y);
      y += 14;
      blankField('Seller Mailing Address', L, TW, y);
      y += 14;
      blankField('Seller Phone Number', L, half, y);
      blankField('Seller Email Address', L + half + 10, half, y);
      y += 18;

      // Buyer block
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text('BUYER:', L, y);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DGRAY);
      y += 6;

      blankField('Buyer Full Legal Name', L, TW, y);
      y += 14;
      blankField('Buyer Mailing Address', L, TW, y);
      y += 14;
      blankField('Buyer Phone Number', L, half, y);
      blankField('Buyer Email Address', L + half + 10, half, y);
      y += 18;

      // ── 2. PROPERTY ──────────────────────────────────────────
      y = checkPage(y + 14);
      y = section('2.  PROPERTY DESCRIPTION', y);

      const propLine = [
        dealData.address,
        dealData.beds      && `${dealData.beds} bed`,
        dealData.baths     && `${dealData.baths} bath`,
        dealData.sqft      && `${Number(dealData.sqft).toLocaleString()} sqft`,
        dealData.yearBuilt && `built ${dealData.yearBuilt}`,
      ].filter(Boolean).join('  ·  ');

      y = bodyText(`Seller agrees to sell and Buyer agrees to purchase the following described real property (the "Property"):`, y);
      blankField('Property Street Address', L, TW, y, dealData.address || '');
      y += 14;
      blankField('City', L, half * 0.6, y, parsedCity);
      blankField('State', L + half * 0.6 + 6, half * 0.2, y, parsedState);
      blankField('Zip Code', L + half * 0.6 + half * 0.2 + 12, half * 0.2, y, parsedZip);
      y += 14;
      blankField('Legal Description / Parcel Number (APN)', L, TW, y);
      y += 14;
      blankField('Property Type (e.g. Single Family, Multi-Family, Condo)', L, TW, y);
      y += 16;

      // ── 3. PURCHASE PRICE ────────────────────────────────────
      y = checkPage(y + 14);
      y = section('3.  PURCHASE PRICE & PAYMENT TERMS', y);

      y = bodyText(`The total purchase price for the Property shall be:`, y);

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text(price, mid, y + 4, { align: 'center' });
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...MGRAY);
      doc.text('(Total Purchase Price)', mid, y + 10, { align: 'center' });
      y += 18;

      autoTable(doc, {
        startY: y,
        body: [
          ['Earnest Money Deposit', '___________________________', 'Due within ___ days of execution'],
          ['Down Payment',          '___________________________', 'Due at closing'],
          ['Loan Amount',           '___________________________', 'Balance financed by Buyer'],
          ['Financing Type',        '___________________________', '(Conventional / FHA / VA / Cash / Other)'],
          ['Lender / Bank Name',    '___________________________', ''],
          ['Pre-Approval Obtained', '   ☐ Yes    ☐ No           ', ''],
        ],
        styles:      { fontSize: 8.5, cellPadding: [4, 5], textColor: DGRAY },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: BLACK, cellWidth: 55 },
          1: { cellWidth: 60 },
          2: { textColor: MGRAY, fontSize: 7.5 },
        },
        margin: { left: L, right: 20 },
        tableLineColor: LGRAY,
        tableLineWidth: 0.2,
      });
      y = doc.lastAutoTable.finalY + 10;

      // ── 4. CLOSING ───────────────────────────────────────────
      y = checkPage(y + 14);
      y = section('4.  CLOSING & POSSESSION', y);

      blankField('Closing Date (on or before)', L, half, y);
      blankField('Possession Date', L + half + 10, half, y);
      y += 14;
      blankField('Title / Escrow Company Name', L, TW, y);
      y += 14;
      blankField('Title Company Address', L, TW, y);
      y += 14;
      y = bodyText(`Closing costs shall be paid as follows: Buyer pays all customary Buyer closing costs. Seller pays all customary Seller closing costs including real estate commissions unless otherwise agreed. All prorations of taxes, rents, HOA fees, and other assessments shall be calculated as of the closing date.`, y);

      // ── 5. EARNEST MONEY ─────────────────────────────────────
      y = checkPage(y + 14);
      y = section('5.  EARNEST MONEY DEPOSIT', y);

      y = bodyText(`Buyer shall deposit earnest money in the amount specified above with the Title/Escrow Company within the number of days specified from the date of execution of this Agreement. The earnest money shall be applied toward the purchase price at closing. In the event of Buyer default, Seller may retain the earnest money as liquidated damages. In the event of Seller default, the earnest money shall be returned to Buyer in full.`, y);

      // ── 6. INSPECTION ────────────────────────────────────────
      y = checkPage(y + 14);
      y = section('6.  INSPECTION CONTINGENCY', y);

      blankField('Inspection Period (days from execution)', L, half, y);
      y += 14;
      y = bodyText(`Buyer shall have the right to conduct, at Buyer's expense, any inspections, investigations, tests, surveys, or reviews of the Property during the Inspection Period. If Buyer disapproves of any condition of the Property, Buyer may, within the Inspection Period: (a) cancel this Agreement and receive a full refund of earnest money; (b) submit a written request to Seller for repairs or price reduction; or (c) waive objections and proceed with purchase.`, y);

      // ── 7. FINANCING CONTINGENCY ─────────────────────────────
      y = checkPage(y + 14);
      y = section('7.  FINANCING CONTINGENCY', y);

      blankField('Financing Contingency Deadline', L, half, y);
      y += 14;
      y = bodyText(`This Agreement is contingent upon Buyer obtaining a written loan commitment for the loan amount specified above at terms acceptable to Buyer by the Financing Contingency Deadline. If Buyer is unable to obtain financing by the deadline, Buyer may cancel this Agreement in writing and receive a full refund of the earnest money deposit. Buyer agrees to make a diligent and good-faith effort to obtain financing.`, y);

      // ── 8. APPRAISAL CONTINGENCY ─────────────────────────────
      y = checkPage(y + 14);
      y = section('8.  APPRAISAL CONTINGENCY', y);

      y = bodyText(`This Agreement is contingent upon the Property appraising at a value equal to or greater than the Purchase Price. If the appraised value is less than the Purchase Price, Buyer may: (a) cancel this Agreement and receive a full refund of earnest money; (b) negotiate a reduced purchase price with Seller; or (c) waive the appraisal contingency and proceed at the agreed purchase price.`, y);

      // ── 9. TITLE ─────────────────────────────────────────────
      y = checkPage(y + 14);
      y = section('9.  TITLE AND CONDITION OF TITLE', y);

      y = bodyText(`Seller shall convey clear and marketable title to the Property to Buyer at closing by a general warranty deed, free and clear of all liens, encumbrances, and defects except: (a) real property taxes not yet due and payable; (b) easements, restrictions, and reservations of record acceptable to Buyer; (c) any existing mortgage(s) to be assumed by Buyer as specified herein. Buyer shall have the right to review a preliminary title report within ten (10) days of execution and to object to any title defects in writing.`, y);

      // ── 10. PROPERTY CONDITION ───────────────────────────────
      y = checkPage(y + 14);
      y = section('10.  PROPERTY CONDITION & AS-IS SALE', y);

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DGRAY);
      doc.text('Property Condition at Time of Sale:', L, y);
      doc.setDrawColor(...BLACK);
      doc.setLineWidth(0.3);
      const boxes = ['As-Is (No Repairs)', 'Seller to Complete Agreed Repairs', 'Credit in Lieu of Repairs'];
      let bx = L + 52;
      boxes.forEach(b => {
        doc.rect(bx, y - 4, 4, 4);
        doc.setFontSize(8);
        doc.text(b, bx + 6, y);
        bx += doc.getTextWidth(b) + 14;
      });
      y += 10;

      y = bodyText(`Seller warrants that, to the best of Seller's knowledge, the Property is free from material defects not disclosed herein. Seller shall provide all legally required property disclosures including but not limited to lead-based paint disclosure (if applicable), natural hazard disclosure, and any known material defects. Seller agrees to maintain the Property in its current condition, reasonable wear and tear excepted, through the closing date.`, y);

      // ── 11. INCLUSIONS / EXCLUSIONS ──────────────────────────
      y = checkPage(y + 14);
      y = section('11.  INCLUSIONS & EXCLUSIONS', y);

      y = bodyText(`The following items are included in the purchase price and shall convey with the Property unless otherwise excluded: all permanently installed fixtures, built-in appliances, window treatments, garage door openers, and any items specifically agreed upon in writing.`, y);
      blankField('Items Specifically Included', L, TW, y);
      y += 14;
      blankField('Items Specifically Excluded', L, TW, y);
      y += 16;

      // ── 12. RISK OF LOSS ─────────────────────────────────────
      y = checkPage(y + 14);
      y = section('12.  RISK OF LOSS', y);

      y = bodyText(`Risk of loss or damage to the Property by fire, windstorm, or other casualty shall remain with Seller until the deed is delivered and possession is transferred to Buyer at closing. In the event of material damage to the Property prior to closing, Buyer may, at Buyer's option: (a) cancel this Agreement and receive a full refund of earnest money; or (b) accept the Property in its damaged condition together with any applicable insurance proceeds.`, y);

      // ── 13. DEFAULT ──────────────────────────────────────────
      y = checkPage(y + 14);
      y = section('13.  DEFAULT', y);

      y = bodyText(`Buyer Default: If Buyer fails to complete the purchase as specified herein without legal justification, Seller may, at Seller's option: (a) retain the earnest money as full and final liquidated damages; (b) institute legal proceedings against Buyer for specific performance; or (c) seek actual damages as permitted by law.`, y);
      y = bodyText(`Seller Default: If Seller fails to complete the sale as specified herein without legal justification, Buyer may, at Buyer's option: (a) receive a full refund of all earnest money; (b) institute legal proceedings against Seller for specific performance; or (c) seek actual damages as permitted by law.`, y);

      // ── 14. DISPUTE RESOLUTION ───────────────────────────────
      y = checkPage(y + 14);
      y = section('14.  DISPUTE RESOLUTION', y);

      y = bodyText(`In the event of any dispute arising out of or relating to this Agreement, the parties agree to first attempt to resolve the dispute through good-faith negotiation. If negotiation is unsuccessful, the parties agree to submit the dispute to mediation before initiating litigation or arbitration. The cost of mediation shall be split equally between the parties. In the event litigation is required, the prevailing party shall be entitled to reasonable attorney's fees and costs.`, y);

      // ── 15. ADDITIONAL TERMS ─────────────────────────────────
      y = checkPage(y + 14);
      y = section('15.  ADDITIONAL TERMS & CONDITIONS', y);

      y = bodyText(`The following additional terms, conditions, and agreements are made part of this Contract:`, y);
      for (let i = 0; i < 6; i++) {
        y = checkPage(y + 10);
        doc.setDrawColor(...LGRAY);
        doc.setLineWidth(0.3);
        doc.line(L, y, R, y);
        y += 9;
      }
      y += 4;

      // ── 16. GENERAL PROVISIONS ───────────────────────────────
      y = checkPage(y + 14);
      y = section('16.  GENERAL PROVISIONS', y);

      const provisions = [
        'Entire Agreement: This Agreement, together with all addenda and attachments, constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior negotiations, representations, warranties, and understandings.',
        'Modifications: No modification or amendment of this Agreement shall be binding unless made in writing and signed by both parties.',
        'Governing Law: This Agreement shall be construed and enforced in accordance with the laws of the state in which the Property is located.',
        'Severability: If any provision of this Agreement is determined to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.',
        'Time is of the Essence: Time is of the essence with respect to all dates and deadlines set forth in this Agreement.',
        'Counterparts / Electronic Signatures: This Agreement may be executed in counterparts, each of which shall be deemed an original. Electronic signatures shall be deemed valid and binding to the same extent as original signatures.',
        'Survival: The representations, warranties, and obligations of the parties shall survive the closing and delivery of the deed.',
        'Notices: All notices required or permitted under this Agreement shall be in writing and shall be delivered by hand, certified mail, overnight courier, or email with confirmation of receipt to the addresses listed herein.',
      ];

      provisions.forEach((p, i) => {
        y = checkPage(y + 12);
        y = bodyText(`${p}`, y, 0);
      });

      y += 4;

      // ── SIGNATURE PAGE ───────────────────────────────────────
      y = checkPage(y + 80);

      doc.setDrawColor(...BLACK);
      doc.setLineWidth(0.5);
      doc.line(L, y, R, y);
      y += 8;

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text('SIGNATURES', mid, y, { align: 'center' });
      y += 6;

      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DGRAY);
      const sigNote = 'By signing below, each party acknowledges that they have read, understood, and agree to be bound by all the terms and conditions of this Real Estate Purchase Contract.';
      const sigNoteLines = doc.splitTextToSize(sigNote, TW);
      doc.text(sigNoteLines, L, y);
      y += sigNoteLines.length * 5.5 + 10;

      const drawSigBlock = (xStart, blockW, role) => {
        const pad = 0;
        const lw  = blockW - 4;
        const cx  = xStart;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...BLACK);
        doc.text(role, cx, y);

        let sy = y + 8;

        // Signature
        doc.setDrawColor(...BLACK);
        doc.setLineWidth(0.5);
        doc.line(cx, sy, cx + lw, sy);
        doc.setFontSize(7.5);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...MGRAY);
        doc.text('Signature', cx, sy + 5);
        sy += 16;

        // First / Last name
        const nameW = (lw - 6) / 2;
        doc.setLineWidth(0.4);
        doc.line(cx, sy, cx + nameW, sy);
        doc.text('First Name', cx, sy + 5);

        doc.line(cx + nameW + 6, sy, cx + lw, sy);
        doc.text('Last Name', cx + nameW + 6, sy + 5);
        sy += 16;

        // Date
        doc.line(cx, sy, cx + nameW, sy);
        doc.text('Date Signed', cx, sy + 5);

        // Printed name
        doc.line(cx + nameW + 6, sy, cx + lw, sy);
        doc.text('Printed Full Name', cx + nameW + 6, sy + 5);
        sy += 16;
      };

      const colW2 = (TW - 16) / 2;
      drawSigBlock(L, colW2, 'SELLER');
      drawSigBlock(L + colW2 + 16, colW2, 'BUYER');

      y += 70;
      y = checkPage(y + 30);

      // Notary block (optional)
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...BLACK);
      doc.text('ACKNOWLEDGMENT (OPTIONAL NOTARIZATION)', L, y);
      doc.setDrawColor(...LGRAY);
      doc.setLineWidth(0.3);
      doc.line(L, y + 2, R, y + 2);
      y += 10;

      const notaryText = 'State of _________________________,  County of _________________________.  On this _____ day of _________________, _______, before me, a Notary Public duly commissioned and sworn, personally appeared _________________________ and _________________________, known to me to be the persons whose names are subscribed to the within instrument, and acknowledged to me that they executed the same as their free and voluntary act for the uses and purposes therein mentioned.';
      const notaryLines = doc.splitTextToSize(notaryText, TW);
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...DGRAY);
      doc.text(notaryLines, L, y);
      y += notaryLines.length * 5.5 + 14;

      blankField('Notary Public Signature', L, colW2, y);
      blankField('My Commission Expires', L + colW2 + 16, colW2, y);
      y += 14;
      blankField('Notary Seal / Stamp Area', L, TW, y);

      addFooter(page);

      const filename = `ProPARV-Contract-${(dealData.address || 'property').split(',')[0].replace(/[^a-z0-9]/gi,'-').replace(/-+/g,'-').toLowerCase().slice(0,35)}.pdf`;
      doc.save(filename);

    } catch (err) {
      console.error('Contract PDF failed:', err);
      alert('Contract generation failed: ' + err.message);
    }

    setGenerating(false);
  };

  return (
    <button
      onClick={generatePDF}
      disabled={generating || !dealData}
      className="btn btn-outline"
      style={{ padding: '8px 18px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}
    >
      {generating
        ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Generating...</>
        : <>📄 Export Contract</>}
    </button>
  );
}
